import type { ProductLine } from "@/platform/productLine";

// The Crisp live-chat seam (BRK-S136) — the ONE place this app knows anything
// about Crisp. Everything here is either pure or takes its host object as an
// argument, so the whole surface is unit-testable without a browser and without
// the vendor script ever loading.
//
// Why a module rather than the snippet Crisp hands you: that snippet is an
// inline <script> in index.html, which would (a) put a vendor host outside the
// one config place (NN #6), (b) load for every visitor whether or not the id is
// configured, and (c) be untestable. The bootstrap below does the same three
// things the snippet does — set the website id, create the command queue, inject
// the client — but reads the id from src/config.ts and is safe to call twice.
//
// Crisp's own cookies/localStorage are its session, not ours; see
// docs/support-chat.md for why that is a documented exception to NN #9.

/** The vendor client. Pinned here so the drift is visible in one diff. */
export const CRISP_SCRIPT_SRC = "https://client.crisp.chat/l.js";

/**
 * Segment tagging every conversation opened from this portal. The marketing
 * site (WWW) may adopt Crisp later; without a portal tag, every routing rule
 * there would have to be expressed as "not the other thing".
 */
export const PORTAL_SEGMENT = "broker-portal";

/**
 * Crisp's command queue. Before `l.js` loads this is a plain array; after, it
 * is Crisp's own object. Both answer `push`, which is the entire contract we
 * need — so commands issued during the first render are queued, not dropped.
 */
export interface CrispQueue {
  push(command: unknown[]): void;
}

/**
 * What the loader needs from `window`. Structural rather than `Window` so tests
 * pass a plain object and assert on the commands (NN #7 — no casts to silence
 * the checker).
 */
export interface CrispHost {
  $crisp?: CrispQueue;
  CRISP_WEBSITE_ID?: string;
  document: Document;
}

/** The non-PII context that rides along with a conversation. */
export interface SupportSessionContext {
  /** Active platform, derived from the route by BRK-S090 — the routing key. */
  productLine: ProductLine;
  /** Resolved brokerage name, or null when not read yet / unauthenticated. */
  brokerageName: string | null;
  /** The route the broker is on, so the agent sees where they got stuck. */
  pathname: string;
}

/** Who is asking, when the portal knows. Null on the pre-login surface. */
export interface SupportBrokerIdentity {
  email: string;
  /** Resolved display name, or null — PortalShell's profile read fails soft. */
  displayName: string | null;
}

/** Whether live chat is active at all: purely "is a website id configured". */
export function supportChatEnabled(websiteId: string | null): boolean {
  return websiteId !== null;
}

/**
 * The segments a Crisp routing rule keys on to pick an agent — Surety → Zach,
 * Commercial → Tyler. The vocabulary is owned HERE so the client and the rule
 * configured in Crisp cannot drift apart silently.
 */
export function supportSegments(productLine: ProductLine): string[] {
  return [PORTAL_SEGMENT, productLine];
}

/**
 * Session data shown beside the conversation in the Crisp inbox. Deliberately
 * limited to what the shell already renders on screen — nothing here is PII the
 * broker hasn't already been shown. A brokerage that hasn't resolved yet is
 * OMITTED rather than sent blank, so the agent sees "not known" as absence
 * instead of an empty row that reads like a data bug.
 */
export function supportSessionData(
  ctx: SupportSessionContext,
): Record<string, string> {
  const data: Record<string, string> = { product_line: ctx.productLine };
  const brokerage = ctx.brokerageName?.trim();
  if (brokerage) data.brokerage = brokerage;
  data.page = ctx.pathname;
  return data;
}

/** Push a command, or do nothing when the widget was never bootstrapped. */
function push(host: CrispHost, command: unknown[]): void {
  host.$crisp?.push(command);
}

/**
 * Set the website id, create the command queue, and inject the client — once.
 * Returns whether this call did the injecting, so a caller can tell a first
 * bootstrap from a no-op. Idempotent because the provider re-renders and two
 * script tags would boot two widgets.
 */
export function bootstrapCrisp(host: CrispHost, websiteId: string): boolean {
  host.CRISP_WEBSITE_ID = websiteId;
  // Never replace an existing queue: it may already hold commands pushed by an
  // earlier render, and after l.js loads it is Crisp's own live object.
  host.$crisp ??= [] as unknown[] as CrispQueue;

  const existing = host.document.querySelector(
    `script[src="${CRISP_SCRIPT_SRC}"]`,
  );
  if (existing) return false;

  const script = host.document.createElement("script");
  script.src = CRISP_SCRIPT_SRC;
  script.async = true;
  host.document.head.appendChild(script);
  return true;
}

/** Open the chat window — what the Support affordances call. */
export function openSupportChat(host: CrispHost): void {
  push(host, ["do", "chat:open"]);
}

/**
 * Publish the routing key + context onto the current Crisp session.
 *
 * The nesting here is Crisp's, and it is one level deeper than it looks:
 * segments and session data are each passed as a single-element array wrapping
 * the payload. Crisp silently ignores a malformed push — no error, no warning —
 * so a flat array would route nothing and report nothing. Pinned by tests.
 */
export function applySupportContext(
  host: CrispHost,
  ctx: SupportSessionContext,
  identity: SupportBrokerIdentity | null,
): void {
  push(host, ["set", "session:segments", [supportSegments(ctx.productLine)]]);
  push(host, [
    "set",
    "session:data",
    [Object.entries(supportSessionData(ctx))],
  ]);

  if (!identity) return;
  push(host, ["set", "user:email", [identity.email]]);
  // Only when resolved — an unset nickname leaves Crisp showing the email,
  // which is better than showing a placeholder name nobody has.
  if (identity.displayName?.trim()) {
    push(host, ["set", "user:nickname", [identity.displayName.trim()]]);
  }
}
